import { NavLink } from 'react-router-dom'
import { LogOut } from 'lucide-react'
import Logo from '../Logo'
import NavBadge from '../nav/NavBadge'
import type { NavGroup } from '../../types/navigation'

interface SidebarProps {
  navGroups: NavGroup[]
  getBadgeCount: (route: string) => number | null
  onSignOut: () => void
  isTeamMember?: boolean
}

export default function Sidebar({ navGroups, getBadgeCount, onSignOut, isTeamMember }: SidebarProps) {
  return (
    <aside className="hidden lg:flex fixed top-0 left-0 bottom-0 w-64 flex-col z-30"
      style={{ background: 'var(--bg-sidebar)' }}>
      {/* Logo */}
      <div className="px-6 py-6" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <NavLink to="/dashboard">
          <Logo size="md" variant="light" />
        </NavLink>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4 px-3" style={{ scrollbarColor: 'rgba(255,255,255,0.15) transparent', overscrollBehavior: 'contain' }}>
        {navGroups.map((group, groupIndex) => (
          <div key={group.label || `group-${groupIndex}`}>
            {groupIndex > 0 && (
              <div style={{ height: 1, background: 'rgba(255,255,255,0.08)', margin: '8px 12px' }} />
            )}
            <div className="space-y-1">
              {group.items.map(({ to, icon, label }) => {
                const badgeCount = getBadgeCount(to)
                return (
                  <NavLink
                    key={to}
                    to={to}
                    className={({ isActive }) =>
                      `sidebar-nav-link group flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] transition-all duration-150 ${
                        isActive ? 'sidebar-nav-link-active' : ''
                      }`
                    }
                  >
                    <span className="w-5 flex items-center justify-center text-base">{icon}</span>
                    <span className="flex-1">{label}</span>
                    <NavBadge visible={badgeCount !== null && badgeCount > 0} />
                  </NavLink>
                )
              })}
            </div>
          </div>
        ))}
      </nav>

      {/* Bottom section */}
      <div className="px-4 py-4" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
        {/* Support is email-only: the in-app ticket feature was deleted
            (0 rows, pre-launch) and replaced with a mailto. */}
        <a
          href="mailto:support@lentals.com"
          className="sidebar-nav-link group flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm transition-all font-normal"
        >
          <span className="w-5 flex items-center justify-center text-base">💬</span>
          Contact support
        </a>
        <NavLink
          to="/settings"
          className={({ isActive }) =>
            `sidebar-nav-link group flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm transition-all ${
              isActive ? 'sidebar-nav-link-active font-semibold' : 'font-normal'
            }`
          }
        >
          <span className="w-5 flex items-center justify-center text-base">⚙️</span>
          Settings
        </NavLink>
        <button
          onClick={onSignOut}
          className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm w-full transition-all hover:bg-white/5"
          style={{ color: 'rgba(255,255,255,0.7)', background: 'transparent', border: 'none', cursor: 'pointer' }}
        >
          <LogOut className="w-5 h-5" />
          Sign out
        </button>
      </div>
    </aside>
  )
}
